import React, { useState, useEffect } from "react";
import { Users, CheckCircle2, Clock, AlertCircle } from "lucide-react";

// Add this component where your Account Status card is located
const AccountStatusCard = () => {
  const [status, setStatus] = useState({
    emailVerified: false,
    gstVerified: false,
    kycSubmitted: false,
    kycApproved: false,
    productsListed: false,
  });

  useEffect(() => {
    // Get values from localStorage
    const sellerEmail = localStorage.getItem("sellerEmail");
    const sellerGstin = localStorage.getItem("sellerGstin");
    const sellerPan = localStorage.getItem("sellerPanNumber");
    const sellerProfile = localStorage.getItem("sellerProfile");

    // Parse seller profile if exists
    let profile = null;
    try {
      profile = sellerProfile ? JSON.parse(sellerProfile) : null;
    } catch (e) {
      console.error("Error parsing seller profile", e);
    }

    // Check if email is verified (email exists in localStorage)
    const emailVerified = !!(
      sellerEmail &&
      sellerEmail !== "null" &&
      sellerEmail !== "undefined"
    );

    // Check if GST is verified (GSTIN exists and is not null/empty)
    const gstVerified = !!(
      sellerGstin &&
      sellerGstin !== "null" &&
      sellerGstin !== "undefined" &&
      sellerGstin.trim() !== ""
    );

    // Check if KYC is submitted (PAN exists)
    const kycSubmitted = !!(
      sellerPan &&
      sellerPan !== "null" &&
      sellerPan !== "undefined" &&
      sellerPan.trim() !== ""
    );

    // Check if KYC is approved (based on status from profile)
    const kycApproved =
      profile?.status === "APPROVED" || profile?.kycStatus === "APPROVED";

    // Check if products are listed (you might need to check from an API or localStorage)
    const productsListed = localStorage.getItem("productsCount")
      ? parseInt(localStorage.getItem("productsCount") || "0") > 0
      : false;

    setStatus({
      emailVerified,
      gstVerified,
      kycSubmitted,
      kycApproved,
      productsListed,
    });
  }, []);

  const statusItems = [
    {
      label: "Email Verified",
      done: status.emailVerified,
      pendingText: "Verify Email",
    },
    {
      label: "GST Verified",
      done: status.gstVerified,
      pendingText: "Verify GST",
    },
    {
      label: "KYC Submitted",
      done: status.kycSubmitted,
      pendingText: "Submit KYC",
    },
    {
      label: "KYC Approved",
      done: status.kycApproved,
      pendingText: "Pending Approval",
    },
    {
      label: "Products Listed",
      done: status.productsListed,
      pendingText: "Add Products",
    },
  ];

  return (
    <div className="inner-card">
      <div className="inner-card-header">
        <Users size={18} className="text-primary" />
        <h2 className="inner-card-title">Account Status</h2>
      </div>
      <div className="flex flex-col gap-3.5">
        {statusItems.map(({ label, done, pendingText }) => (
          <div key={label} className="flex items-center gap-3 group">
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 border transition-colors ${
                done
                  ? "bg-emerald-50 border-emerald-200 text-emerald-500"
                  : "bg-slate-50 border-slate-200 text-slate-300 group-hover:border-primary/20"
              }`}
            >
              {done ? <CheckCircle2 size={12} /> : <Clock size={12} />}
            </div>
            <span
              className={`text-sm font-medium transition-colors ${
                done ? "text-slate-900" : "text-slate-400"
              }`}
            >
              {label}
            </span>
            {!done && (
              <span className="badge badge-warning ml-auto !py-0.5 !px-1.5 !text-[9px]">
                {pendingText}
              </span>
            )}
            {done && label === "KYC Approved" && (
              <span className="badge badge-success ml-auto !py-0.5 !px-1.5 !text-[9px]">
                Approved
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Summary Section */}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500">Overall Progress:</span>
          <span className="font-semibold text-primary">
            {Object.values(status).filter((v) => v)?.length}/
            {statusItems?.length} Complete
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
          <div
            className="bg-primary rounded-full h-1.5 transition-all duration-500"
            style={{
              width: `${(Object.values(status).filter((v) => v)?.length / statusItems?.length) * 100}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AccountStatusCard;
